import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, BarChart3 } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { useBattlesQuery, useProfileMeQuery } from '@/services/qbotApi';
import { BattleRow } from '@/components/cr/BattleRow';
import { cn } from '@/lib/cn';
import type { QbotApiError } from '@/types/qbot';

function isQbotApiError(err: unknown): err is QbotApiError {
  return typeof err === 'object' && err !== null && 'data' in err;
}

/**
 * Recent battles for the linked player, paginated client-side over the
 * fixed 25-entry CR API window. The `analyze` toggle adds a header card
 * with the current-deck win-rate computed across the same window.
 *
 * Tag resolution: we don't accept a tag in the URL (the user wants their
 * own data). We pull it from /profile/me; if there's no linked player,
 * redirect to /cr/link.
 */
export function CRBattles() {
  const { t } = useTranslation('cr');
  const haptics = useHaptics();
  const [page, setPage] = useState(1);
  const [analyze, setAnalyze] = useState(false);
  const pageSize = 10;

  const { data: profile, isLoading: profileLoading, error: profileError } = useProfileMeQuery();
  const tag = profile?.savedTag ?? profile?.player?.tag?.replace('#', '') ?? '';

  const { data, isFetching, error } = useBattlesQuery(
    { tag, page, pageSize, analyze },
    { skip: !tag },
  );

  if (profileLoading) {
    return <div className="px-4 py-6 text-sm text-fg-muted">{t('battles.loading')}</div>;
  }
  if (profileError) {
    const code = isQbotApiError(profileError) ? profileError.data?.error?.code : undefined;
    if (code === 'NOT_LINKED' || code === 'NOT_FOUND') return <Navigate to="/cr/link" replace />;
  }

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto px-4 py-4">
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-fg">{t('battles.title')}</h1>
        <button
          type="button"
          onClick={() => {
            haptics.selection();
            setAnalyze((v) => !v);
            setPage(1);
          }}
          className={cn(
            'flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-semibold transition-colors',
            analyze ? 'bg-brand text-brand-fg' : 'bg-bg-secondary text-fg-muted',
          )}
        >
          <BarChart3 className="h-3.5 w-3.5" />
          {t('battles.analyze')}
        </button>
      </header>

      {analyze && data?.analysis && (
        <div className="rounded-xl bg-bg-secondary p-3 text-xs">
          <p className="mb-1 font-semibold text-fg">
            {t('battles.currentDeckWinRate')}
          </p>
          <div className="flex items-center gap-3 text-fg-muted tabular-nums">
            <span>
              <span className="text-base font-bold text-emerald-300">{data.analysis.winRate.wins}</span> W
            </span>
            <span>
              <span className="text-base font-bold text-rose-300">{data.analysis.winRate.losses}</span> L
            </span>
            <span>
              <span className="text-base font-bold text-fg">{data.analysis.winRate.winRate}%</span> WR
            </span>
            {data.analysis.winRate.streak !== 0 && (
              <span
                className={cn(
                  'rounded px-1.5 py-0.5 text-[10px] font-bold',
                  data.analysis.winRate.streak > 0
                    ? 'bg-emerald-500/20 text-emerald-300'
                    : 'bg-rose-500/20 text-rose-300',
                )}
              >
                {data.analysis.winRate.streak > 0
                  ? t('battles.streak.win', { count: data.analysis.winRate.streak })
                  : t('battles.streak.loss', { count: Math.abs(data.analysis.winRate.streak) })}
              </span>
            )}
          </div>
        </div>
      )}

      {!!error && (
        <p className="rounded-md bg-rose-500/10 p-3 text-sm text-rose-300">{t('battles.error')}</p>
      )}

      <ul className="flex flex-col gap-2">
        {data?.battles.map((b, i) => (
          <li key={`${b.battleTime}-${i}`}>
            <BattleRow battle={b} playerTag={tag} />
          </li>
        ))}
      </ul>

      {data && data.battles.length === 0 && (
        <p className="px-4 py-6 text-center text-sm text-fg-muted">{t('battles.empty')}</p>
      )}

      {data && data.pagination.totalPages > 1 && (
        <nav className="mt-auto flex items-center justify-between rounded-lg bg-bg-secondary p-2">
          <button
            type="button"
            disabled={page <= 1 || isFetching}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="flex h-8 w-8 items-center justify-center rounded-md text-fg disabled:opacity-30"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="text-xs text-fg-muted tabular-nums">
            {t('battles.page', { page, total: data.pagination.totalPages })}
          </span>
          <button
            type="button"
            disabled={page >= data.pagination.totalPages || isFetching}
            onClick={() => setPage((p) => p + 1)}
            className="flex h-8 w-8 items-center justify-center rounded-md text-fg disabled:opacity-30"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </nav>
      )}
    </div>
  );
}
